import { useEffect, useRef, forwardRef, useImperativeHandle, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTheme } from '../../theme/ThemeContext';

// Add styles to make attribution links visible in both themes
const mapStyles = `
  .dark .mapboxgl-ctrl-attrib-inner a {
    color: white !important;
  }
  .mapboxgl-ctrl-attrib-inner a {
    color: #1f2937 !important;
  }
`;

export interface GlobeOracleHandle {
  flyToLocation: (coordinates: [number, number]) => void;
  resetUserInteraction: () => void;
  resize: () => void;
  isReady: () => boolean;
}

export interface LocationData {
  name: string;
  coordinates: [number, number];
  zoom?: number;
}

interface GlobeOracleProps {
  initialLocation?: [number, number];
  initialZoom?: number;
  targetLocation?: [number, number];
  locations?: LocationData[];
  onLocationChange?: (location: LocationData) => void;
  onMapClick?: (coordinates: [number, number]) => void;
  isVisible?: boolean;
}

const GlobeOracle = forwardRef<GlobeOracleHandle, GlobeOracleProps>((props, ref) => {
  const {
    initialLocation = [8.5417, 47.3769],
    initialZoom = 1.5,
    targetLocation,
    locations = [],
    onLocationChange,
    onMapClick,
    isVisible = true,
  } = props;
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const lastTargetLocationRef = useRef<string | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const { theme } = useTheme();

  // Track if map has been initialized
  const [mapInitialized, setMapInitialized] = useState(false);

  // Save map state (position and zoom)
  const [mapState, setMapState] = useState({
    center: initialLocation,
    zoom: initialZoom,
  });

  useEffect(() => {
    if (!mapContainerRef.current || !isVisible || mapInitialized) return;

    mapboxgl.accessToken =
      'pk.eyJ1IjoibWFydmluZ2FibGVyIiwiYSI6ImNtODRpaW55NjBzdjcyanM3a2l3bDAxYXAifQ.3zrJdRYQt-lD-cP8phh1dw';

    // Use saved map state or defaults
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      zoom: mapState.zoom,
      center: mapState.center,
      pitch: 0,
      bearing: 0,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      projection: 'globe',
      attributionControl: false,
    });

    map.addControl(
      new mapboxgl.AttributionControl({
        customAttribution: ['© Jua'],
      }),
    );

    mapRef.current = map;
    setMapInitialized(true);

    map.on('style.load', () => {
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14,
      });

      map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
      map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    });

    // Save map state when user moves it
    const saveMapState = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      setMapState({
        center: [center.lng, center.lat],
        zoom: zoom,
      });
    };

    map.on('moveend', saveMapState);
    map.on('zoomend', saveMapState);

    // Add click handler for map clicks
    if (onMapClick) {
      map.on('click', (e) => {
        const { lng, lat } = e.lngLat;
        onMapClick([lng, lat]);
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        setMapInitialized(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, isVisible]);

  // Handle target location changes - only fly to NEW locations
  useEffect(() => {
    if (!targetLocation || !mapRef.current || !mapInitialized) return;

    const locationKey = `${targetLocation[0]},${targetLocation[1]}`;
    const lastLocation = lastTargetLocationRef.current;

    // Only fly if this is a NEW location
    if (lastLocation !== locationKey) {
      flyToLocation(targetLocation);
      lastTargetLocationRef.current = locationKey;
    }
  }, [targetLocation, mapInitialized]);

  const addMarkersAndLabels = useCallback(
    (map: mapboxgl.Map, locations: LocationData[]) => {
      // Remove existing markers
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      // Remove existing labels layer if it exists
      if (map.getLayer('location-labels')) {
        map.removeLayer('location-labels');
      }
      if (map.getSource('location-labels')) {
        map.removeSource('location-labels');
      }

      // Add new markers
      locations.forEach((location) => {
        const marker = new mapboxgl.Marker({
          color: '#E34290', // Pink color to match forecast theme
          scale: 0.8,
        })
          .setLngLat(location.coordinates)
          .addTo(map);

        // Track marker for cleanup
        markersRef.current.push(marker);

        // Handle marker click - stop propagation to prevent map click
        marker.getElement().addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent map click handler from firing
          if (onLocationChange) {
            onLocationChange(location);
          }
        });
      });

      // Add text labels for all locations
      if (locations.length > 0) {
        map.addSource('location-labels', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: locations.map((location) => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: location.coordinates,
              },
              properties: {
                name: location.name,
              },
            })),
          },
        });

        map.addLayer({
          id: 'location-labels',
          type: 'symbol',
          source: 'location-labels',
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [1.5, 0],
            'text-anchor': 'left',
            'text-size': 12,
          },
          paint: {
            'text-color': '#374151', // Dark gray for light mode
            'text-halo-color': '#ffffff',
            'text-halo-width': 2,
          },
        });
      }
    },
    [onLocationChange],
  );

  // Add location markers
  useEffect(() => {
    if (!mapRef.current || !mapInitialized || !isVisible) return;

    const map = mapRef.current;

    // Additional safety check - ensure map container is still valid
    if (!mapContainerRef.current || !mapContainerRef.current.offsetParent) return;

    // Check if style is loaded before adding sources
    if (!map.isStyleLoaded()) {
      // Wait for style to load, then try again
      const styleLoadHandler = () => {
        map.off('style.load', styleLoadHandler);
        // Trigger this effect again after style loads
        setTimeout(() => {
          if (mapRef.current) {
            addMarkersAndLabels(map, locations);
          }
        }, 100);
      };
      map.on('style.load', styleLoadHandler);
      return;
    }

    addMarkersAndLabels(map, locations);
  }, [locations, mapInitialized, isVisible, addMarkersAndLabels]);

  const flyToLocation = (coordinates: [number, number]) => {
    if (!mapRef.current) return;

    mapRef.current.flyTo({
      center: coordinates,
      zoom: 12,
      essential: true,
    });
  };

  const resetUserInteraction = () => {
    // Reset to initial position
    setMapState({
      center: initialLocation,
      zoom: initialZoom,
    });
  };

  const resize = () => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
  };

  const isReady = () => {
    return mapInitialized && !!mapRef.current;
  };

  useImperativeHandle(ref, () => ({
    flyToLocation,
    resetUserInteraction,
    resize,
    isReady,
  }));

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <style>{mapStyles}</style>
      <div
        ref={mapContainerRef}
        style={{
          height: '100%',
          width: '100%',
          overflow: 'hidden',
        }}
      />
    </div>
  );
});

export default GlobeOracle;
